import { type JSX, useState } from 'react';

import { useAtomValue, useSetAtom } from 'jotai';

import { filtersListAtom } from '@core/Atoms/Plp/FiltersListAtom';
import type { Tenant } from '@core/Entities/App/Tenants.entity';
import type { FilterKey } from '@core/Entities/Search/SearchFilters/Filter.entity';
import { openQuickFilterAtom } from '@plp/Atoms/QuickFilterOpenAtom';
import { FilterLink } from '@plp/Components/Filters/Components/FilterLink/FilterLink';
import type {
  PlpFiltersMessagesKeys,
  PlpFiltersMessagesProps,
} from '@plp/Components/Filters/FilterPanel.entity';
import { trackFilterEvent } from '@plp/Components/Filters/Filters.tracking';
import type { Item } from '@plp/Components/MultiSelect/MultiSelect.entity';
import { getArrayFilterValue } from '@plp/Utils/Filters/GetArrayFilterValue';

import { useFilterLink } from './UseFilterLink/UseFilterLink';
import { useUpdateFilters } from './UseUpdateFilters';

const OTHER_LABELS = ['other', 'others'];

interface MultiSelectOption {
  id: string;
  label: string;
  selected: boolean;
  count: number;
  iconName?: string;
  labelKey?: string;
  renderLabel?: (name: string) => JSX.Element | React.ReactNode;
}

const useMultiSelectFilters = (
  filterName: FilterKey,
  options: MultiSelectOption[],
  strings: PlpFiltersMessagesProps,
  isPlpFiltersSeoEnabled: boolean,
  tenant: Tenant | undefined,
  listEnhanceFn?: (optObj: MultiSelectOption, selected: string[], noOfOptions: number) => Item,
  applyButton?: boolean,
) => {
  const setOpenQuickFilter = useSetAtom(openQuickFilterAtom);
  const filtersList = useAtomValue(filtersListAtom);

  const { updateFilters } = useUpdateFilters();
  const { getLinkForFilterValue } = useFilterLink({
    filterId: filterName,
    isPlpFiltersSeoEnabled,
    tenant,
  });
  const [pendingSelections, setPendingSelections] = useState<Record<string, boolean>>({});

  const defaultListEnhanceFn = (
    optObj: MultiSelectOption,
    selected: string[],
    noOfOptions: number,
  ) => {
    const isOnlyOption = noOfOptions === 1;
    const labelKey = optObj.labelKey ?? optObj.id;
    const intlLabel = strings[labelKey as PlpFiltersMessagesKeys] || optObj.label;
    const link = getLinkForFilterValue({
      filterValue: optObj.id,
      resultCount: optObj.count,
      isOnlyOption,
    });

    return {
      renderLabel: <FilterLink link={link}>{intlLabel}</FilterLink>,
      ...optObj,
      label: intlLabel,
      selected: selected?.includes(optObj.id) || isOnlyOption,
      ...(isOnlyOption ? { disabled: true } : {}),
    } as Item;
  };

  const selectedItems = (getArrayFilterValue(filterName as FilterKey, filtersList) ||
    []) as string[];

  const updateSelections = (id: string, isChecked: boolean) => {
    const updatedSelections = [...selectedItems];
    if (isChecked) {
      updatedSelections.push(id);
    } else {
      const doorIndex = updatedSelections.indexOf(id);
      if (doorIndex > -1) updatedSelections.splice(doorIndex, 1);
    }
    return updatedSelections;
  };

  const applyPendingSelections = (id: string, isChecked: boolean) => {
    setPendingSelections(prev => {
      const newSelections = { ...prev };
      newSelections[id] = isChecked;
      return newSelections;
    });
  };

  const updateFiltersAndTrack = (newItems: string[]) => {
    trackFilterEvent(filterName, newItems, filtersList);
    updateFilters([{ id: filterName, value: newItems }]);
  };

  const handleSelect = (id: string, isChecked: boolean) => {
    const filter = options.find(opt => opt.id.includes(id));
    if (filter) {
      const updatedSelections = updateSelections(filter.id, isChecked);

      applyButton
        ? applyPendingSelections(filter.id, isChecked)
        : updateFiltersAndTrack(updatedSelections);
    } else {
      const updatedSelections = isChecked
        ? [...selectedItems, id]
        : selectedItems.filter((item: string) => item !== id);

      applyButton
        ? setPendingSelections(prev => ({ ...prev, [id]: isChecked }))
        : updateFiltersAndTrack(updatedSelections);
    }
  };

  const handleMultiSelect = (selections: Record<string, boolean>) => {
    let updatedSelections = [...selectedItems];

    Object.entries(selections).forEach(([id, isChecked]) => {
      if (isChecked && !updatedSelections.includes(id)) {
        updatedSelections.push(id);
      } else if (!isChecked && updatedSelections.includes(id)) {
        updatedSelections = updatedSelections.filter(item => item !== id);
      }
    });
    trackFilterEvent(filterName, [...Object.values(updatedSelections)], filtersList);
    updateFilters([{ id: filterName, value: updatedSelections }]);
  };

  const listWithSelected = options
    .map(optObj =>
      listEnhanceFn
        ? listEnhanceFn({ ...optObj }, selectedItems, options.length)
        : defaultListEnhanceFn(optObj, selectedItems, options.length),
    )
    .sort((a, b) => {
      if (OTHER_LABELS.includes(a.label.toLowerCase())) return 1;
      if (OTHER_LABELS.includes(b.label.toLowerCase())) return -1;
      return a.label > b.label ? 1 : -1;
    });

  const handleApply = () => {
    handleMultiSelect(pendingSelections);
    setPendingSelections({});
    setOpenQuickFilter(null);
  };

  return {
    handleSelect,
    handleMultiSelect,
    handleApply,
    listWithSelected,
  };
};

export { useMultiSelectFilters };
export type { MultiSelectOption };
